// One board entry, opened.
//
// Clicking a task used to tick it off there and then. That is right on the
// Daily Board, where somebody is standing in the aisle with a phone working
// through a list — and wrong on the Schedule, where the same click is how you
// find out what a row actually is. A tap that changes data before you have
// read anything is a tap you make by accident.
//
// So a click opens this instead, and marking done is a button in it. The rest
// of what you can do to the row lives here too, rather than being split between
// a click that completed and a different click that edited.
//
// What the buttons are depends on what the row is, and on what the API can
// actually do:
//
//   task     mark done / not done, and cancel — there is no task editor yet,
//            and PATCH /tasks takes a status and nothing else
//   lesson   edit and delete, both of which exist
//   service  neither: a due date stops being due when the visit is recorded on
//            the horse, so the only useful move is to go there

import { useState } from 'react';
import { formatTime, formatDay, BLOCKS } from '../../lib/schedule';
import { colorClass } from '../../lib/lessonTypes';

const STATUS_LABEL = {
  pending: 'Not done yet',
  done: 'Done',
  skipped: 'Skipped',
  cancelled: 'Cancelled',
  scheduled: 'Scheduled',
  tentative: 'Tentative',
  completed: 'Completed',
  due: 'Due',
};

function spineClass(item) {
  if (item.kind === 'task') return 'bg-emerald-400';
  if (item.kind === 'service') return 'bg-slate-400';
  return colorClass(item.lessonTypeColor, 'spine');
}

function Field({ label, children }) {
  if (!children) return null;
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-[10px] font-medium uppercase tracking-wider text-ink-400 w-16 shrink-0 pt-0.5">
        {label}
      </span>
      <span className="text-sm text-ink-800 min-w-0">{children}</span>
    </div>
  );
}

export default function ItemModal({
  item,
  canEditLesson,
  canToggleTask,
  busy,
  onToggleDone,
  onCancelTask,
  onEdit,
  onDelete,
  onOpenHorse,
  onClose,
}) {
  // Inline rather than a second modal on top of this one: the question is
  // about the thing already on screen, and stacking dialogs to ask it hides
  // the row you are deciding about.
  const [confirming, setConfirming] = useState(null); // 'delete' | 'cancel'

  const isTask = item.kind === 'task';
  const isService = item.kind === 'service';
  const blockLabel = BLOCKS.find(([id]) => id === item.block)?.[1] ?? null;
  const notes = item.raw?.notes ?? null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-md rounded-t-xl sm:rounded-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={item.title}
      >
        <div className="flex items-start gap-3 px-5 py-4 border-b border-ink-100">
          <span className={'w-1 h-10 rounded-full shrink-0 mt-0.5 ' + spineClass(item)} aria-hidden />
          <div className="min-w-0 flex-1">
            <h2 className="font-medium text-lg leading-snug break-words">{item.title}</h2>
            <p className="text-sm text-ink-500">
              {formatDay(item.date, { weekday: 'long', month: 'short', day: 'numeric' })}
              {item.time ? ` · ${formatTime(item.time)}` : blockLabel ? ` · ${blockLabel}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-md text-ink-500 hover:bg-ink-100 text-xl leading-none shrink-0"
          >
            ×
          </button>
        </div>

        <div className="px-5 py-4 space-y-2">
          <Field label={isService ? 'Service' : isTask ? 'Task' : 'Lesson'}>{item.detail}</Field>
          {!isService && <Field label="Who">{item.person}</Field>}
          {isService && <Field label="Horse">{item.raw?.horseName}</Field>}
          {isService && <Field label="Provider">{item.raw?.providerName}</Field>}
          <Field label="Status">{STATUS_LABEL[item.status] ?? item.status}</Field>
          <Field label="Notes">{notes}</Field>
        </div>

        {confirming ? (
          <div className="px-5 py-4 border-t border-ink-100">
            <p className="text-sm text-ink-700">
              {confirming === 'delete'
                ? 'Delete this lesson? It goes to the archive and can be restored there.'
                : "Cancel this task? It stays on the board, greyed out, so nobody wonders where it went."}
            </p>
            <div className="flex items-center gap-2 mt-3">
              <button
                type="button"
                className="btn-secondary text-rose-700 border-rose-200 hover:border-rose-400"
                disabled={busy}
                onClick={confirming === 'delete' ? onDelete : onCancelTask}
              >
                {busy ? 'Working…' : confirming === 'delete' ? 'Delete It' : 'Cancel It'}
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={busy}
                onClick={() => setConfirming(null)}
              >
                Never mind
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2 px-5 py-4 border-t border-ink-100">
            {isTask && canToggleTask && (
              <button type="button" className="btn-primary" disabled={busy} onClick={onToggleDone}>
                {item.done ? 'Mark Not Done' : 'Mark Done'}
              </button>
            )}
            {!isTask && !isService && canEditLesson && (
              <button type="button" className="btn-primary" disabled={busy} onClick={onEdit}>
                Edit
              </button>
            )}
            {isService && (
              <button type="button" className="btn-primary" disabled={busy} onClick={onOpenHorse}>
                Open {item.raw?.horseName ?? 'Horse'}
              </button>
            )}

            {isTask && canToggleTask && item.status !== 'cancelled' && (
              <button
                type="button"
                className="btn-secondary text-rose-700 border-rose-200 hover:border-rose-400"
                disabled={busy}
                onClick={() => setConfirming('cancel')}
              >
                Cancel Task
              </button>
            )}
            {!isTask && !isService && canEditLesson && (
              <button
                type="button"
                className="btn-secondary text-rose-700 border-rose-200 hover:border-rose-400"
                disabled={busy}
                onClick={() => setConfirming('delete')}
              >
                Delete
              </button>
            )}

            <button type="button" className="btn-secondary ml-auto" onClick={onClose}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
